import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import Styles from './PostPreviewDrawer.module.scss'
import { DesignChannel } from 'components/NewFileModal/Types'
import { NoIntegration } from './NoIntegration'
import { IntegrationActions } from './IntegrationActions'
import { Skeleton } from 'antd'
import { PostOptionsPage, PostPreviewProps } from './Types'
import cx from 'classnames'
import { Preview } from './Preview'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ChevronLeft } from 'blaze-ui/icons/24'
import { PostPreviewProvider, usePostPreviewContext } from './PostPreviewContext'
import { StalePreviewOverlay } from './components/StalePreviewOverlay'
import { Text } from 'blaze-ui/Text/Text'
import { MobileFooter } from './MobileFooter'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { PostPreviewDrawerV2 } from 'components/PostPreviewModalV2/PostPreviewDrawerV2'

const PostPreviewDrawer = ({
  fileId,
  channel,
  pollPreviewContentStream = true,
  showSuccessToast = true,
  ...drawerProps
}: PostPreviewProps & DrawerProps) => {
  const { page, scheduledPost, isLoading, provider, providerName, hasIntegration, previewIsStale } =
    usePostPreviewContext()

  const showActions = hasIntegration && scheduledPost

  const title = () => {
    switch (page) {
      case PostOptionsPage.PostDetails:
        return 'Post Now'
      case PostOptionsPage.Schedule:
        return 'Edit scheduled post'
      case PostOptionsPage.Reminder:
        return 'Set a reminder to post'
      default:
        return 'Post Now'
    }
  }

  if (!provider || !providerName) return null

  return (
    <Drawer.Root modal={false} {...drawerProps}>
      <Drawer.Portal>
        <Drawer.Content fullHeight onOpenAutoFocus={(e) => e.preventDefault()}>
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top>
              <Drawer.Close icon={ChevronLeft} label="" />
              <Drawer.Title asChild>
                <Heading level={5}>{title()}</Heading>
              </Drawer.Title>
            </Drawer.Top>
            <Drawer.Container>
              <div className={Styles.body}>
                {isLoading || !scheduledPost ? (
                  <div className={cx(Styles.actions, Styles.skeletonsContainer)} data-testid="skeleton">
                    <Skeleton active />
                    <Skeleton active />
                  </div>
                ) : (
                  <div className={Styles.actions}>
                    {showActions ? (
                      <IntegrationActions fileId={fileId} channel={channel as DesignChannel} />
                    ) : (
                      <NoIntegration provider={provider} providerName={providerName} fileId={fileId} />
                    )}
                  </div>
                )}
                <div className={Styles.preview}>
                  <Text variant="label" color="var(--dark-60)">
                    Preview
                  </Text>
                  <Preview
                    channel={channel}
                    loading={isLoading}
                    post={scheduledPost}
                    integration={scheduledPost?.external_integration}
                    pollPreviewContentStream={pollPreviewContentStream}
                    hideConnectButton
                  />
                  {previewIsStale && <StalePreviewOverlay />}
                </div>
              </div>
              {showActions && <MobileFooter fileId={fileId} channel={channel as DesignChannel} />}
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

const PostPreviewDrawerContainer = (props: PostPreviewProps) => {
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2_mobile')

  if (isPostPreviewV2) {
    return <PostPreviewDrawerV2 {...props} />
  }

  return (
    <PostPreviewProvider {...props}>
      <PostPreviewDrawer {...props} />
    </PostPreviewProvider>
  )
}

export { PostPreviewDrawerContainer as PostPreviewDrawer }
